import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

export const MocktailsPage = () => {
  const mocktails = [
    {
      number: "1",
      name: "The Cotswold Kick",
      tags: "Apple • Ginger • Spice",
      description: "A crisp blend of cloudy apple, fresh citrus and our signature fire cider. Warming, balanced and quietly powerful — the perfect introduction to botanical heat.",
      tagline: "Best seller energy. First-timers love this.",
      color: "from-amber-500 to-orange-600"
    },
    {
      number: "2",
      name: "Orchard Fire Fizz",
      tags: "Ginger • Lime • Apple",
      description: "Bright apple and lime lifted with gentle bubbles and a slow-building fire cider warmth. Refreshing up front, fiery on the finish.",
      tagline: "Feels familiar, drinks exciting.",
      color: "from-lime-500 to-green-600"
    },
    {
      number: "3",
      name: "Fire & Tonic",
      tags: "Herbal • Bitter • Clean",
      description: "A sophisticated, alcohol-free take on a classic. Light tonic, subtle sweetness and a herbal fire cider backbone make this crisp, dry and very grown-up.",
      tagline: "Your G&T replacement.",
      color: "from-cyan-500 to-teal-600"
    },
    {
      number: "4",
      name: "Blood Orange Ember",
      tags: "Citrus • Spice • Depth",
      description: "Deep blood orange meets warming spice and fire cider heat. Rich, vibrant and comforting — especially good on colder days.",
      tagline: "Looks incredible. Sells itself.",
      color: "from-red-500 to-orange-600"
    },
    {
      number: "5",
      name: "The Fire Mule",
      tags: "Ginger • Lime • Heat",
      description: "Bold ginger beer, fresh lime and a generous hit of fire cider. Sharp, spicy and unapologetically warming.",
      tagline: "For people who ask \"can I really feel it?\"",
      color: "from-yellow-500 to-amber-600"
    },
    {
      number: "6",
      name: "Botanical Immunity Spritz",
      tags: "Elderflower • Herbs • Light Fire",
      description: "Soft elderflower and soda layered with botanical fire cider. Light, floral and gently warming — endlessly drinkable.",
      tagline: "The \"I'll have another one\" mocktail.",
      color: "from-purple-400 to-pink-500"
    }
  ];

  return (
    <div className="paper-texture min-h-screen">
      {/* Header */}
      <div className="bg-surface py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-xl text-primary">Get Creative</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-secondary mt-2" data-testid="mocktails-title">
            Fire Cider Mocktails
          </h1>
          <p className="text-muted-foreground mt-4 text-lg">
            Discover delicious ways to enjoy our fire cider in refreshing, alcohol-free cocktails.
          </p>
        </div>
      </div>

      {/* Mocktails Grid */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {mocktails.map((mocktail, index) => (
            <div 
              key={index} 
              className="bg-white rounded-xl border overflow-hidden hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-1"
              data-testid={`mocktail-${index + 1}`}
            >
              {/* Number Badge */}
              <div className={`h-2 bg-gradient-to-r ${mocktail.color}`}></div>
              
              <div className="p-8">
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${mocktail.color} flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white font-bold text-lg">{mocktail.number}</span>
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl font-semibold text-secondary">{mocktail.name}</h2>
                    <p className="text-primary font-medium text-sm mt-1">{mocktail.tags}</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-muted-foreground leading-relaxed">
                  {mocktail.description}
                </p>

                {/* Tagline */}
                <p className="font-accent text-xl text-primary mt-4">
                  {mocktail.tagline}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-secondary rounded-xl p-12">
          <h3 className="font-heading text-3xl font-bold text-secondary-foreground">Ready to mix?</h3>
          <p className="text-amber-100/80 mt-3 text-lg">
            Grab a bottle of our fire cider and start creating these delicious mocktails at home.
          </p>
          <Link to="/products">
            <Button size="lg" className="mt-6" data-testid="shop-cta">
              Shop Fire Cider
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
